import * as React from 'react';
import { IEnvironment } from '../../../interfaces/IEnvironment';
import UploaderService from '../../../services/UploaderService';
import { IOrgAssetPermissionFolder } from '../../../interfaces/OrgAssets/IOrgAssetPermissionFolder';
import { ITreeViewItem } from '../../../interfaces/ITreeViewItem';
import { IAToggle } from '../../toggle/Toggle';
import { LocalizationService } from '../../../services/LocalizationService';
import { IATreeview } from '../../treeview/Treeview';
import { IASpinner } from '../../spinner/Spinner';
import { IAComposerPlaceholder } from '../../composer/placeholders/ComposerPlaceholder';
import { IAButton } from '../../button/Button';
import ImageService from '../../../services/ImageService';
import { Environment } from '../../../services/Environment';
import { IAMediaSelectorPreview } from '../mediaSelectorPreview/MediaSelectorPreview';
import * as styles from "./MediaSelectorUpload.css";

export interface IAMediaSelectorUploadProps {
  token: string;
  tenant: string;
  environment: IEnvironment;
  fileType: "image" | "video";
  highlightColor: string;
  uploadUrl: string;
  // When set, uploaded images wider or taller than this (in pixels) are
  // downscaled client-side before upload.
  maxImageDimension?: number;
  onSelect: (url: string) => void;
  onUploadingChange?: (isUploading: boolean) => void;
}

export interface IAMediaSelectorUploadState {
  isLoadingUploadFolders: boolean;
  uploadToOrgAsset: boolean;
  uploadFolders: ITreeViewItem[];
  uploadFolderSelected: ITreeViewItem;
  uploadError: boolean;
  filePreview: string;
  fileToUpload: File;
  isUploadingFile: boolean;
}

export class IAMediaSelectorUpload extends React.Component<IAMediaSelectorUploadProps, IAMediaSelectorUploadState> {
  private readonly localizationService = new LocalizationService();

  constructor(props: IAMediaSelectorUploadProps) {
    super(props);
    this.state = {
      isLoadingUploadFolders: false,
      uploadToOrgAsset: false,
      uploadFolders: undefined,
      uploadFolderSelected: undefined,
      uploadError: undefined,
      filePreview: undefined,
      fileToUpload: undefined,
      isUploadingFile: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private getOrgAssetsWithUploadPermissions(): void {
    this.setState({ isLoadingUploadFolders: true });
    const uploadFolders: ITreeViewItem[] = [];
    UploaderService.getOrgAssetsWithUploadPermissions(this.props.environment, this.props.token, this.props.tenant).then((data) => {
      if (data) {
        data.forEach((folder) => {
          uploadFolders.push(this.convertToTreeViewObject(folder));
        });
      }
      this.setState({ uploadFolders: uploadFolders, isLoadingUploadFolders: false });
    });
  }

  private convertToTreeViewObject(folder: IOrgAssetPermissionFolder, title?: string): ITreeViewItem {
    const titleToUse = title ?? folder.title;
    const children = [];
    if (folder.folders && folder.folders.length > 0) {
      folder.folders.forEach((subFolder: any) => {
        children.push(this.convertToTreeViewObject(subFolder, titleToUse));
      });
    }
    return {
      title: folder.title ? folder.title : folder.name,
      children: children,
      checked: false,
      name: folder.serverRelativeUrl ? folder.serverRelativeUrl : folder.title,
      orgAssetInfo: {
        id: folder.id,
        site: folder.serverRelativeUrl.replace("/sites/", "").split("/")[0],
        title: titleToUse
      }
    }
  }

  public render(): JSX.Element {
    const isImage = this.props.fileType === "image";
    return (
      <>
        <div className={styles.IA_MediaSelectorUploadContainer}>
          {this.state.filePreview
          ?
            <IAMediaSelectorPreview
              tenant={this.props.tenant}
              token={this.props.token}
              environment={this.props.environment}
              fileType={this.props.fileType}
              highlightColor={this.props.highlightColor}
              url={this.state.filePreview}
              onClearImage={() => {
                URL.revokeObjectURL(this.state.filePreview);
                this.setState({
                  filePreview: undefined,
                  fileToUpload: undefined,
                  isUploadingFile: false
                }, () => this.props.onUploadingChange?.(false));
              }}
            />
          :
            <IAComposerPlaceholder
              id={"MediaSelectorUpload"}
              highlightColor={this.props.highlightColor}
              isActive
              onClick={() => {}}
              borderRadius={10}
              enableUpload
              fileType={this.props.fileType}
              icon={{
                name: isImage ? "Picture" : "Video",
                size: isImage ? 60 : 40
              }}
              text={isImage ? this.localizationService.strings.Composer_DropAnImageHere : this.localizationService.strings.Composer_DropVideoHere}
              uploadButtonText={this.localizationService.strings.MediaSelector_BrowseLocalFiles}
              uploadFileCallback={async (file) => this.setState({
                fileToUpload: file,
                filePreview: URL.createObjectURL(file)
              })}
            />
          }
        </div>
        <IAToggle
          checked={this.state.uploadToOrgAsset}
          label={this.localizationService.strings.Composer_UploadToOrgAssets}
          color={this.props.highlightColor}
          style={{ marginTop: 25 }}
          disabled={this.state.isLoadingUploadFolders || this.state.isUploadingFile}
          onChange={() => {
            this.setState({ uploadToOrgAsset: !this.state.uploadToOrgAsset }, () => {
              if (this.state.uploadToOrgAsset && !this.state.uploadFolders) {
                this.getOrgAssetsWithUploadPermissions();
              }
              if (!this.state.uploadToOrgAsset) {
                this.setState({
                  uploadFolderSelected: undefined
                });
              }
            });
          }}
        />
        {this.state.uploadToOrgAsset &&
          <>
            {this.state.isLoadingUploadFolders ? <IASpinner color={this.props.highlightColor}/> :
              <IATreeview
                highlightColor={this.props.highlightColor}
                actionType="Radio"
                structureType="Folder"
                disabled={this.state.isUploadingFile}
                onSelect={(item: ITreeViewItem) => this.setState({ uploadFolderSelected: item })}
                onChange={(uploadFolders) => this.setState({ uploadFolders })}
                items={this.state.uploadFolders}
                style={{
                  marginLeft: 40
                }}
              />
            }
          </>
        }
        <IAButton
          buttonColor={this.props.highlightColor || "#025159"}
          borderRadius={5}
          label={isImage ? this.localizationService.strings.MediaSelector_UploadAndUseImage : this.localizationService.strings.MediaSelector_UploadAndUseVideo}
          disbaled={!this.state.fileToUpload || this.state.isUploadingFile}
          showSpinner={this.state.isUploadingFile}
          onClick={() => {
            this.setState({isUploadingFile: true}, () => {
              this.props.onUploadingChange?.(true);
              let uploadUrl = this.props.uploadUrl;
              if (this.state.uploadFolderSelected) {
                // upload to the chosen org asset folder
                const site = this.state.uploadFolderSelected.orgAssetInfo.site;
                const relativeUrl = this.state.uploadFolderSelected.name;
                uploadUrl = `${Environment.getEnvironmentForMainAPI(this.props.environment)}/api/${this.props.tenant}.sharepoint.com/image?site=${site}&relativeUrl=${encodeURIComponent(relativeUrl)}`
              }

              const uploadPromise = this.props.maxImageDimension
                ? ImageService.resizeImageFile(this.state.fileToUpload, this.props.maxImageDimension)
                : Promise.resolve(this.state.fileToUpload);

              uploadPromise
                .then((fileToUpload) => ImageService.uploadSelectedFile(fileToUpload, uploadUrl))
                .then((url) => {
                  this.props.onSelect(url);
                })
                .catch(() => this.setState({uploadError: true, isUploadingFile: false}, () => this.props.onUploadingChange?.(false)));
            });
          }}
          style={{
            marginTop: 15
          }}
        />
      </>
    );
  }

  componentWillUnmount(): void {
    if (this.state.filePreview) {
      URL.revokeObjectURL(this.state.filePreview);
    }
  }
}
